import { useAction, useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Button, useHapticFeedback } from '@blade/ui-kit'
import { useBreakpoint } from '@root/shared'
import { mediaMinTablet } from '@root/shared/ui-kit'

import type { CreateWalletFlowModel } from '../../atoms'
import type { ClassNameProps } from '@root/shared/react'

type CreateWalletAcknowledgementsProps = {
  model: CreateWalletFlowModel
} & ClassNameProps

export const CreateWalletSeedPhraseConfirm: React.FC<
  CreateWalletAcknowledgementsProps
> = ({ model, className }) => {
  const { t } = useTranslation()
  const breakpoint = useBreakpoint()
  const haptics = useHapticFeedback()

  const [requestState] = useAtom(model.confirmStateAtom)
  const [mnemonicQuiz] = useAtom(model.mnemonicQuizAtom)
  const [isValid] = useAtom(model.confirmationValidAtom)
  const [quizAnswers] = useAtom(model.selectedWordAtom)

  const selectWord = useAction(model.selectWordAction)
  const finish = useAction(model.confirmStateAtom.execute)

  return (
    <Root className={className}>
      <Description>
        {t('wallet.create.seed-phrase-words-selection-description')}
      </Description>
      <WordSelectionContainer>
        {mnemonicQuiz.items.map((it) => {
          return (
            <div key={it.index}>
              <WordTitle>
                <span> {t('wallet.create.word')}</span>
                <span> {it.index + 1}</span>
              </WordTitle>
              <WordSelection>
                {it.options.map((option) => {
                  return (
                    <Word
                      key={option}
                      data-selected={quizAnswers[it.index] === option}
                      onClick={() => {
                        haptics.impactOccurred('light')
                        selectWord(it.index, option)
                      }}
                    >
                      {option}
                    </Word>
                  )
                })}
              </WordSelection>
            </div>
          )
        })}
      </WordSelectionContainer>
      <Button
        fullWidth
        disabled={!isValid}
        onClick={() => finish()}
        variant="solid"
        color="primary"
        size={breakpoint === 'mobile' ? 'h40' : 'h36'}
        radius="rounded"
        busy={requestState.loading}
      >
        {t('common.next')}
      </Button>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  padding-inline: 12px;
  align-items: start;
  overflow: auto;

  ${mediaMinTablet(css`
    padding-inline: 12px;
  `)}
`

const Word = styled.div`
  padding: 10px 10px;
  cursor: pointer;

  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;

  background: var(--backgroundA-4);
  color: var(--text-11);
  &[data-selected='true'] {
    background: var(--primary-9);
    color: var(--primary-contrast);
  }

  text-align: center;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;

  ${mediaMinTablet(css`
    color: var(--text-11);

    text-align: center;

    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;

    &[data-selected='true'] {
      color: var(--text-12);
    }
  `)}
`

const WordSelection = styled.div`
  display: grid;
  gap: 9px;
  grid-template-columns: 1fr 1fr 1fr;

  ${mediaMinTablet(css`
    gap: 12px;
  `)}
`

const WordSelectionContainer = styled.div`
  display: grid;
  gap: 24px;

  ${mediaMinTablet(css`
    gap: 18px;
  `)}
`

const WordTitle = styled.div`
  color: var(--text-12);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  margin-bottom: 12px;

  ${mediaMinTablet(css`
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    margin-bottom: 10px;
  `)}
`

const Description = styled.div`
  color: var(--text-9);

  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  margin-bottom: 16px;

  ${mediaMinTablet(css`
    font-size: 11px;
    font-weight: 400;
    line-height: 16px;
    margin-bottom: 14px;
  `)}
`
